import { useContext, useEffect, useReducer, useRef } from 'react';
import { message } from 'antd';
import { useApolloClient } from 'react-apollo';
import useUserGroups from 'hooks/useUserGroups';
import { OrgDataContext } from 'context/orgData';
import { cleanGraphQLErrorMsg } from 'common/util';
import {
    canDeletePerson,
    deletePerson,
    describePersonDeletion,
    loadDeletionImpact,
    PersonDeletionImpact,
    PersonDeletionBlockedError,
    sameDeletionImpact,
    personDeletionSuccess
} from './operation';

/** Stateful inputs shared by Persons details and the passenger editor. */
export type PersonDeleteOptions = {
    /** Persisted Person ID; missing/deleted person references hide the action. */
    personID?: string;
    /** Refreshes the owning view after confirmed deletion; refresh failure does not retry deletion. */
    onDeleted?: () => void | Promise<unknown>;
    /** Prevents activation during an unrelated save/delete operation in the owning view. */
    disabled?: boolean;
};

type State = {
    visible: boolean;
    busy: boolean;
    loading: boolean;
    preview?: PersonDeletionImpact;
    deleted: boolean;
    acknowledged: boolean;
    error?: string;
};

type Action =
    | { type: 'open' | 'cancel' | 'submit' | 'succeeded' }
    | { type: 'failed'; error: string }
    | { type: 'preview'; preview: PersonDeletionImpact }
    | { type: 'acknowledge'; checked: boolean };

/** Keeps confirmation, request and completion transitions together without mutating prior state. */
function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'acknowledge':
            return { ...state, acknowledged: action.checked };

        case 'open':
            return { ...state, visible: true, loading: true, preview: undefined, error: undefined, acknowledged: false };

        case 'cancel':
            return { ...state, visible: false, busy: false, loading: false, preview: undefined, acknowledged: false };

        case 'submit':
            return { ...state, busy: true, error: undefined };

        case 'failed':
            return { ...state, busy: false, loading: false, preview: undefined, error: action.error, acknowledged: false };

        case 'preview':
            return {...state, busy: false, loading: false, preview: action.preview, acknowledged: false};

        case 'succeeded':
            return { ...state, busy: false, visible: false, deleted: true };
    }
}

/**
 * Owns person-delete confirmation and request transitions separately from the UI template.
 *
 * @param options - Persisted Person ID, owning-view refresh callback and external disabled state.
 * @returns Reducer state, visibility/availability guards and open/cancel/confirm handlers.
 *
 * @remarks
 * Cancel sends no mutation. The synchronous ref guard prevents double confirmation before a
 * reducer update renders and prevents canceling an active request. It is a local request guard,
 * not a distributed lock. Confirmation reloads the preview and requires a new acknowledgement
 * when affected records, displayed details or blockers change. Preview errors keep deletion disabled until a new preview succeeds;
 * success disables repeats before refreshing the view. Refresh errors never retry deletion.
 * Parent views key the component by Person ID so completion state cannot leak to another person.
 *
 * Permissions here control presentation; AppSync and the server enforce explicit delete permissions,
 * America/Chicago eligibility cutoff (including DST), and deleted-reference exclusions. The hook
 * uses the server preview to gate confirmation and never attempts rollback after partial writes.
 */
export function usePersonDelete({
    personID,
    onDeleted,
    disabled
}: PersonDeleteOptions) {
    const [groups] = useUserGroups();
    const orgData = useContext(OrgDataContext);
    const client = useApolloClient();
    const [state, dispatch] = useReducer(reducer, { visible: false, busy: false, loading: false, deleted: false, acknowledged: false });
    const inFlight = useRef(false);
    const previewRequest = useRef(0);
    const orgID = orgData?.getActiveOrgID();
    const accountOrganizationID = orgData?.organization?._id;
    const canDelete = !!personID && canDeletePerson(groups);
    const unavailable = !!disabled || state.busy || state.loading || state.deleted || !orgID;

    // Ignore late previews after cancel, a new request, an organization change or unmount.
    useEffect(() => {
        previewRequest.current++;
        if (!inFlight.current) dispatch({type: 'cancel'});
        return () => { previewRequest.current++; };
    }, [personID, orgID, accountOrganizationID, canDelete]);

    async function open() {
        if (!canDelete || unavailable || inFlight.current) return;
        const request = ++previewRequest.current;
        dispatch({ type: 'open' });
        try {
            const preview = await loadDeletionImpact(client, groups, personID, orgID, accountOrganizationID);
            if (request === previewRequest.current) dispatch({type: 'preview', preview});
        } catch (error) {
            if (request === previewRequest.current) {
                dispatch({type: 'failed', error: cleanGraphQLErrorMsg(error.message)});
            }
        }
    }

    function cancel() {
        if (!inFlight.current) {
            previewRequest.current++;
            dispatch({ type: 'cancel' });
        }
    }

    const canConfirm = state.acknowledged && canDelete && !unavailable && !!state.preview && !state.preview.blockingMessage
        && !state.preview.blockers?.length;

    async function confirm() {
        if (inFlight.current || !canConfirm) {
            return;
        }

        const request = previewRequest.current;
        inFlight.current = true;
        dispatch({ type: 'submit' });

        let successMessage: string;
        try {
            // Recheck immediately before writing, including the display details the user reviewed.
            const preview = await loadDeletionImpact(client, groups, personID, orgID, accountOrganizationID);
            if (request !== previewRequest.current) {
                inFlight.current = false;
                dispatch({type: 'cancel'});
                return;
            }

            if (!sameDeletionImpact(state.preview, preview)) {
                inFlight.current = false;
                dispatch({type: 'preview', preview});
                message.warning('The affected records have changed. Please review the updated changes and acknowledge them again before deleting.');
                return;
            }

            const changes = await deletePerson(client, groups, personID, orgID);
            successMessage = personDeletionSuccess(changes);
        } catch (err) {
            if (err instanceof PersonDeletionBlockedError) {
                try {
                    const preview = await describePersonDeletion(client, err.changes, personID, accountOrganizationID);
                    dispatch({type: 'preview', preview});
                } catch (lookupError) {
                    dispatch({type: 'failed', error: `${err.message} ${cleanGraphQLErrorMsg(lookupError.message)}`});
                }
            } else {
                dispatch({ type: 'failed', error: cleanGraphQLErrorMsg(err.message) });
            }
            inFlight.current = false;
            return;
        }

        dispatch({ type: 'succeeded' });
        message.success(successMessage);

        try {
            await onDeleted?.();
        } catch (_) {
            message.warning(
                'The person was deleted, but the list could not refresh. Refresh the page to see the latest records.'
            );
        }
    }

    return { state, canDelete, unavailable, canConfirm, open, cancel, confirm,
        acknowledge: (checked: boolean) => dispatch({type: 'acknowledge', checked}) };
}
